/* cc-loyalty-surfaces.js, 24 Sept 2026
   Behaviour for the Curlfriend Rewards lines rendered by
   snippets/cc-loyalty-surface.liquid. Loaded once from
   snippets/cart-drawer.liquid, in the initial page HTML, never through a
   section re-render (a <script> that arrives by innerHTML never runs).

   Three jobs, all delegated or observed so they survive every re-render:

   1. ANNOUNCE CART CHANGES. The cart line lives inside regions Dawn
      destroys and rebuilds on a quantity change, so an aria-live attribute
      on the line itself would never announce: it arrives with its text
      already inside it. Instead one persistent live region is created here,
      appended to <body> and never re-rendered, and written to after the
      update lands. Detection is a MutationObserver on <cart-drawer> and on
      #main-cart-footer, which catches every path that re-renders them:
      Dawn's CartItems, CartDrawer.renderContents, and cc-cart-recos.js
      (which does not publish a pubsub event, so pubsub alone would miss it).

   2. PDP VARIANT CHANGE. product-info.js publishes PUB_SUB_EVENTS
      .variantChange with the freshly rendered section HTML. The line's
      contents are copied from that HTML, so the Liquid stays the only
      place the maths lives and a per-variant price is always right.

   3. ANALYTICS. dataLayer pushes for GTM-WZVSS3XK. State names and the
      surface only; no points values, no customer data.
*/
(function () {
  if (window.ccLoyReady) return;
  window.ccLoyReady = true;

  function push(event, params) {
    window.dataLayer = window.dataLayer || [];
    var o = { event: event };
    for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) o[k] = params[k];
    window.dataLayer.push(o);
  }

  var viewed = {};
  function viewOnce(event, key, params) {
    if (viewed[key]) return;
    viewed[key] = true;
    push(event, params);
  }

  /* The sentence only, without the link label, so the announcement does not
     end on a bare "Join free". */
  function textOf(el) {
    if (!el) return '';
    var copy = el.cloneNode(true);
    copy.querySelectorAll('[data-cc-loy-cta]').forEach(function (a) { a.remove(); });
    return copy.textContent.replace(/\s+/g, ' ').trim();
  }

  /* ---------- live region ---------- */
  var live = null;
  function liveRegion() {
    if (live && document.body.contains(live)) return live;
    live = document.createElement('div');
    live.id = 'cc-loy-live';
    live.className = 'visually-hidden';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    document.body.appendChild(live);
    return live;
  }
  function announce(text) {
    var region = liveRegion();
    region.textContent = '';
    /* Clearing first and writing on a later tick makes a repeated message
       announce again. */
    setTimeout(function () { region.textContent = text; }, 120);
  }

  /* ---------- cart lines ---------- */
  function drawerEl() { return document.querySelector('cart-drawer'); }
  function drawerOpen() {
    var d = drawerEl();
    return !!(d && d.classList.contains('active'));
  }
  function cartLine(ctx) {
    if (ctx === 'drawer') {
      var d = drawerEl();
      return d ? d.querySelector('[data-cc-loy-surface="drawer"]') : null;
    }
    var f = document.getElementById('main-cart-footer');
    return f ? f.querySelector('[data-cc-loy-surface="cart"]') : null;
  }

  var last = { page: null, drawer: null };

  function cartView(ctx) {
    var el = cartLine(ctx);
    if (!el) return;
    if (ctx === 'drawer' && !drawerOpen()) return;
    var state = el.getAttribute('data-cc-loy-state');
    viewOnce('loyalty_cart_line_view', 'cart:' + ctx + ':' + state, {
      loyalty_state: state,
      loyalty_context: ctx
    });
  }

  function checkCart(ctx) {
    var el = cartLine(ctx);
    var text = textOf(el);
    var prev = last[ctx];
    last[ctx] = text;
    if (prev === null) return; /* first read, nothing changed yet */
    if (text === prev) return;
    cartView(ctx);
    /* Announce only an update to a line that was already there, and only
       in the surface the shopper is using. */
    if (!text || !prev) return;
    var active = ctx === 'drawer' ? drawerOpen() : !drawerOpen();
    if (active) announce(text);
  }

  function watch(target, ctx) {
    if (!target || !('MutationObserver' in window)) return;
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () { checkCart(ctx); }, 60);
    }).observe(target, { childList: true, subtree: true });
  }

  function initCart() {
    last.page = textOf(cartLine('page'));
    last.drawer = textOf(cartLine('drawer'));
    cartView('page');

    watch(document.getElementById('main-cart-footer'), 'page');

    var d = drawerEl();
    watch(d, 'drawer');
    if (d && 'MutationObserver' in window) {
      /* Drawer view fires when it opens, not when it sits hidden in the DOM. */
      new MutationObserver(function () {
        if (drawerOpen()) cartView('drawer');
      }).observe(d, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* ---------- PDP ---------- */
  function initPdp() {
    document.querySelectorAll('[data-cc-loy-surface="pdp"]').forEach(function (el) {
      var state = el.getAttribute('data-cc-loy-state');
      if (state) viewOnce('loyalty_pdp_line_view', 'pdp:' + state, { loyalty_state: state });
    });

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var data = event && event.data;
      if (!data || !data.html || !data.sectionId) return;
      var id = 'cc-loy-pdp-' + data.sectionId;
      var source = data.html.getElementById(id);
      var target = document.getElementById(id);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
      target.setAttribute('data-cc-loy-state', source.getAttribute('data-cc-loy-state') || '');
    });
  }

  /* ---------- clicks ---------- */
  document.addEventListener('click', function (event) {
    var cta = event.target.closest('[data-cc-loy-cta]');
    if (!cta) return;
    var host = cta.closest('[data-cc-loy-surface]') || cta;
    var surface = host.getAttribute('data-cc-loy-surface');
    var state = host.getAttribute('data-cc-loy-state') || '';
    if (surface === 'cart' || surface === 'drawer') {
      push('loyalty_cart_cta_click', {
        loyalty_state: state,
        loyalty_context: surface === 'drawer' ? 'drawer' : 'page'
      });
    } else if (surface === 'pdp') {
      push('loyalty_pdp_join_click', { loyalty_state: state });
    } else if (surface === 'pill') {
      push('loyalty_header_pill_click', {});
    } else if (surface === 'account') {
      push('loyalty_account_strip_click', { loyalty_state: state });
    }
  });

  function init() {
    initCart();
    initPdp();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
